/**
 * Division.js
 * PSP/CRE Tool v2.0 - Hierarchical Force Structure
 * 
 * Division class - operational formation of 3-5 brigades
 * Typically 10,000-20,000 personnel, has organic Role 3 medical facility
 * 
 * Medical Hierarchy Position:
 * - Division → Role 3 (Combat Support Hospital)
 * - Provides definitive surgical care, intensive care
 * - Receives casualties from brigade Role 2 facilities
 * - May also have Role 2 Enhanced (Forward Surgical Team)
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: November 26, 2025
 * Version: 1.0
 */

import { BaseUnit } from './BaseUnit.js';

export class Division extends BaseUnit {
  /**
   * Create a Division
   * 
   * @param {Object} config - Division configuration
   * @param {string} config.id - Unique identifier
   * @param {string} config.name - Division name
   * @param {number} config.division_number - Number within corps (1, 2, 3...)
   * @param {number} [config.strength=15000] - Personnel strength
   * @param {number} [config.position_feba_km=100] - Distance from FEBA (Romanian default: 100km)
   */
  constructor(config) {
    super({
      ...config,
      type: 'DIVISION',
      strength: config.strength || 15000,
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : 100
    });
    
    // Division-specific properties
    this.division_number = config.division_number;  // Position within corps (1, 2, 3)
    this.parent_corps_id = null;                    // Set when added to corps
    this.brigades = [];                             // Array of Brigade instances
    
    // Medical facilities
    this.role2_enhanced_facility = null;            // Optional - some divisions have this
    this.role2_enhanced_facility_id = null;
    this.role3_facility = null;                     // Primary division hospital
    this.role3_facility_id = null;
    
    // Division type
    this.division_type = config.division_type || 'INFANTRY'; // INFANTRY, ARMOR, MECHANIZED, AIRBORNE
  }
  
  // ============================================================================
  // BRIGADE MANAGEMENT METHODS
  // ============================================================================
  
  /**
   * Add brigade to this division
   * Establishes parent-child relationship
   * 
   * @param {Brigade} brigade - Brigade instance to add
   */
  addBrigade(brigade) {
    this.addChild(brigade);
    this.brigades.push(brigade);
    brigade.parent_division_id = this.id;
  }
  
  /**
   * Get all brigades in this division
   * 
   * @returns {Brigade[]} Array of brigade instances
   */
  getBrigades() {
    return this.brigades;
  }
  
  /**
   * Get brigade by number
   * 
   * @param {number} brigade_number - Brigade number (1, 2, 3...)
   * @returns {Brigade|null} Brigade instance or null if not found
   */
  getBrigadeByNumber(brigade_number) {
    return this.brigades.find(brig => brig.brigade_number === brigade_number) || null;
  }
  
  /**
   * Get all battalions across all brigades
   * Flattens the hierarchy for easy iteration
   * 
   * @returns {Battalion[]} Array of all battalion instances in division
   */
  getAllBattalions() {
    return this.brigades.flatMap(brig => brig.getBattalions());
  }
  
  /**
   * Get total strength of all brigades
   * 
   * @returns {number} Sum of all brigade current strengths
   */
  getTotalBrigadeStrength() {
    return this.brigades.reduce((sum, brig) => sum + brig.getAccurateStrength(), 0);
  }
  
  /**
   * Get division strength as sum of brigade strengths plus division HQ
   * 
   * @returns {number} Total division strength
   */
  getAccurateStrength() {
    const brigade_total = this.getTotalBrigadeStrength();
    const hq_strength = this.current_strength - this.brigades.reduce((sum, brig) => sum + brig.strength, 0);
    return brigade_total + Math.max(0, hq_strength);
  }
  
  // ============================================================================
  // FACILITY ID GENERATION
  // ============================================================================
  
  /**
   * Generate unique facility ID for this division's Role 3
   * Format: role3_div{X}
   * 
   * @returns {string} Facility ID
   */
  generateRole3FacilityId() {
    return `role3_div${this.division_number}`;
  }
  
  /**
   * Generate unique facility ID for this division's Role 2 Enhanced (if has one)
   * Format: role2e_div{X}
   * 
   * @returns {string} Facility ID
   */
  generateRole2EnhancedFacilityId() {
    return `role2e_div${this.division_number}`;
  }
  
  /**
   * Generate human-readable facility name for Role 3
   * 
   * @returns {string} Name like "Division 1 Hospital (Role 3)"
   */
  generateRole3FacilityName() {
    if (!this.parent) return `Division ${this.division_number} Hospital (Role 3)`;
    
    const corps = this.parent;
    return `${corps.name} / Division ${this.division_number} Hospital (Role 3)`;
  }
  
  /**
   * Get Role 3 distance from FEBA
   * 
   * @returns {number} Distance in kilometers
   */
  getRole3Distance() {
    return this.position_feba_km;
  }
  
  // ============================================================================
  // MEDICAL INTEGRATION
  // ============================================================================
  
  /**
   * Link Role 3 medical facility to this division
   * 
   * @param {Role3Facility} facility - Medical facility object
   */
  linkRole3Facility(facility) {
    this.role3_facility = facility;
    this.role3_facility_id = facility.facility_id;
  }
  
  /**
   * Link Role 2 Enhanced facility to this division (optional)
   * 
   * @param {Role2EnhancedFacility} facility - Medical facility object
   */
  linkRole2EnhancedFacility(facility) {
    this.role2_enhanced_facility = facility;
    this.role2_enhanced_facility_id = facility.facility_id;
  }
  
  /**
   * Check if division has Role 3 medical support configured
   * 
   * @returns {boolean} True if Role 3 facility linked
   */
  hasMedicalSupport() {
    return this.role3_facility !== null;
  }
  
  /**
   * Check if division has Role 2 Enhanced
   * 
   * @returns {boolean} True if Role 2 Enhanced facility linked
   */
  hasRole2Enhanced() {
    return this.role2_enhanced_facility !== null;
  }
  
  /**
   * Get Role 3 medical facility capacity status
   * 
   * @returns {Object|null} Capacity info or null if no facility
   */
  getRole3CapacityStatus() {
    if (!this.role3_facility) return null;
    
    return {
      facility_id: this.role3_facility_id,
      facility_name: this.role3_facility.facility_name,
      beds_total: this.role3_facility.beds,
      beds_available: this.role3_facility.getAvailableBeds(),
      bed_utilization: this.role3_facility.getBedUtilization(),
      icu_beds: this.role3_facility.icu_beds,
      surgical_teams: this.role3_facility.surgical_teams,
      operational: this.role3_facility.operational
    };
  }
  
  /**
   * Get all Role 2 Basic facilities from brigades
   * 
   * @returns {Array} Array of Role 2 Basic facility objects
   */
  getAllBrigadeRole2Facilities() {
    return this.brigades
      .filter(brig => brig.role2_basic_facility !== null)
      .map(brig => brig.role2_basic_facility);
  }
  
  /**
   * Get all Role 1 facilities from all battalions
   * 
   * @returns {Array} Array of Role 1 facility objects
   */
  getAllBattalionRole1Facilities() {
    return this.brigades.flatMap(brig => brig.getAllBattalionRole1Facilities());
  }
  
  // ============================================================================
  // AGGREGATION METHODS
  // ============================================================================
  
  /**
   * Get combat effectiveness status for all brigades
   * 
   * @returns {Object} Summary of brigade effectiveness
   */
  getBrigadeEffectivenessStatus() {
    const total = this.brigades.length;
    const effective = this.brigades.filter(brig => brig.isCombatEffective()).length;
    const degraded = this.brigades.filter(brig => {
      const pct = brig.getStrengthPercentage();
      return pct >= 50 && pct < 70;
    }).length;
    const ineffective = this.brigades.filter(brig => {
      const pct = brig.getStrengthPercentage();
      return pct > 0 && pct < 50;
    }).length;
    const destroyed = this.brigades.filter(brig => brig.current_strength === 0).length;
    
    return {
      total_brigades: total,
      combat_effective: effective,
      degraded: degraded,
      ineffective: ineffective,
      destroyed: destroyed,
      effectiveness_percentage: total > 0 ? (effective / total) * 100 : 0
    };
  }
  
  /**
   * Get combat effectiveness for all battalions in division
   * 
   * @returns {Object} Summary of battalion effectiveness across division
   */
  getAllBattalionEffectivenessStatus() {
    const all_battalions = this.getAllBattalions();
    const total = all_battalions.length;
    const effective = all_battalions.filter(bn => bn.isCombatEffective()).length;
    const degraded = all_battalions.filter(bn => {
      const pct = bn.getStrengthPercentage();
      return pct >= 50 && pct < 70;
    }).length;
    const ineffective = all_battalions.filter(bn => {
      const pct = bn.getStrengthPercentage();
      return pct > 0 && pct < 50;
    }).length;
    const destroyed = all_battalions.filter(bn => bn.current_strength === 0).length;
    
    return {
      total_battalions: total,
      combat_effective: effective,
      degraded: degraded,
      ineffective: ineffective,
      destroyed: destroyed,
      effectiveness_percentage: total > 0 ? (effective / total) * 100 : 0
    };
  }
  
  /**
   * Calculate total casualties across all brigades and battalions
   * 
   * @returns {number} Total casualties
   */
  getTotalCasualties() {
    return this.brigades.reduce((sum, brig) => {
      return sum + brig.getTotalBattalionCasualties();
    }, 0);
  }
  
  // ============================================================================
  // SERIALIZATION
  // ============================================================================
  
  /**
   * Convert to JSON for storage
   * 
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      ...super.toJSON(),
      division_number: this.division_number,
      division_type: this.division_type,
      parent_corps_id: this.parent_corps_id,
      role2_enhanced_facility_id: this.role2_enhanced_facility_id,
      role3_facility_id: this.role3_facility_id,
      brigades_count: this.brigades.length,
      battalions_count: this.getAllBattalions().length,
      brigades: this.brigades.map(brig => brig.toJSON()),
      total_brigade_strength: this.getTotalBrigadeStrength(),
      brigade_effectiveness: this.getBrigadeEffectivenessStatus(),
      battalion_effectiveness: this.getAllBattalionEffectivenessStatus()
    };
  }
  
  /**
   * Get detailed summary
   * 
   * @returns {Object} Detailed summary
   */
  getDetailedSummary() {
    const base_summary = this.getSummary();
    
    return {
      ...base_summary,
      division_number: this.division_number,
      division_type: this.division_type,
      brigades_count: this.brigades.length,
      battalions_count: this.getAllBattalions().length,
      total_brigade_strength: this.getTotalBrigadeStrength(),
      brigade_effectiveness: this.getBrigadeEffectivenessStatus(),
      battalion_effectiveness: this.getAllBattalionEffectivenessStatus(),
      total_casualties: this.getTotalCasualties(),
      medical_support: this.hasMedicalSupport(),
      has_role2_enhanced: this.hasRole2Enhanced(),
      role3_capacity: this.getRole3CapacityStatus(),
      role3_facility: {
        id: this.role3_facility_id,
        name: this.generateRole3FacilityName(),
        distance_feba_km: this.getRole3Distance()
      },
      brigades_summary: this.brigades.map(brig => ({
        id: brig.id,
        name: brig.name,
        brigade_number: brig.brigade_number,
        battalions_count: brig.battalions.length,
        strength: brig.getAccurateStrength(),
        strength_percentage: brig.getStrengthPercentage(),
        effectiveness: brig.getCombatEffectivenessRating()
      }))
    };
  }
}

/**
 * USAGE EXAMPLES:
 * 
 * import { Division } from './models/force/Division.js';
 * import { Brigade } from './models/force/Brigade.js';
 * import { Battalion } from './models/force/Battalion.js';
 * 
 * // Create a division
 * const division = new Division({
 *   id: 'div1',
 *   name: '1st Infantry Division',
 *   division_number: 1,
 *   division_type: 'INFANTRY',
 *   strength: 15000,
 *   position_feba_km: 100
 * });
 * 
 * // Create brigades
 * const brig1 = new Brigade({
 *   id: 'brig1',
 *   name: '1st Brigade',
 *   brigade_number: 1,
 *   strength: 4000
 * });
 * 
 * // Add battalions to brigade
 * for (let i = 1; i <= 3; i++) {
 *   const bn = new Battalion({
 *     id: `bn${i}`,
 *     name: `Battalion ${i}`,
 *     battalion_number: i,
 *     strength: 800
 *   });
 *   brig1.addBattalion(bn);
 * }
 * 
 * // Add brigade to division
 * division.addBrigade(brig1);
 * 
 * // Get all battalions in division
 * const all_bns = division.getAllBattalions();
 * console.log(`Division has ${all_bns.length} battalions`);
 * 
 * // Check effectiveness
 * console.log(division.getBrigadeEffectivenessStatus());
 * console.log(division.getAllBattalionEffectivenessStatus());
 * 
 * // Generate facility ID
 * console.log(division.generateRole3FacilityId()); // "role3_div1"
 */
